import React from 'react';
import styled from 'styled-components';

import { Command, tabActions } from '@store/slices';
import { Label } from '@common/Label';
import { Box, Divider, Stack } from '@mui/material';
import { useDispatch } from 'react-redux';
import { Select } from '@common/Select';
import { Option } from '@common/BucketInput/BucketField';
import { Button } from '@common/Button';
import { v4 as uuidv4 } from 'uuid';
import { useNodeData } from '@common/hooks/useNodeData';
import { AICompletionJsonMode, AICompletionNodeState, NodeField } from '@components/Flow/Nodes';
import { Expression } from '@common/Expression';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  grid-gap: 8px;

  & .MuiInputAdornment-root {
    margin-right: 10px;
  }
`;

const Arguments = styled.div`
  margin-top: 0.5rem;
`;

const ArgumentBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  border-radius: 6px;
`;

const Notice = styled.p<{ error?: boolean }>`
  font-size: 12px;
  color: ${({ theme, error }) => (error ? theme.telnyx.red : theme.text.subtext)};
  padding-left: 12px;
`;

interface Props {
  command: Command;
  field: NodeField;
  index: number;
}

export const ToggleJSON: React.FC<Props> = ({ command, field }) => {
  const dispatch = useDispatch();

  const { getOptions, dataEdges } = useNodeData(command.node.id);

  const id = field.id;
  const state = command.state[id as keyof AICompletionNodeState] as AICompletionJsonMode;
  const args = state.args || [];
  const enabled = state.enabled;
  const options = getOptions(command.node.id);
  const label = 'JSON Arguments';

  const description = [
    'When JSON mode is enabled, the LLM will output a JSON response instead of a text response.',
    'This is useful for getting the LLM to generate structured data so that it can be used in Branch nodes.',
    'It works the same way as the Tool Arguments.',
  ].join('\n');

  const addArgument = () => {
    dispatch(tabActions.setSaved(false));

    const value = {
      id: uuidv4(),
      name: '',
      description: '',
      enumerator: '',
      type: 'string' as const,
    };

    dispatch(
      tabActions.nodeInputNestedArrayPush({
        nodeId: command.node.id,
        fieldId: id,
        nestedFieldName: 'args',
        value: value,
      })
    );
  };

  const updateField = (nestedFieldId: string, fieldName: string, value: string) => {
    if (fieldName === 'enumerator') {
      const enums = value.split(',');
      const enumFormatted = [];

      for (const item of enums) {
        enumFormatted.push(item.replace(/\n/g, ' ').trim());
      }

      value = enumFormatted.join(',\n');
    }

    dispatch(
      tabActions.nodeInputNestedArrayUpdate({
        nodeId: command.node.id,
        fieldId: id,
        nestedFieldName: 'args',
        nestedFieldId,
        fieldName,
        value,
      })
    );
  };

  const deleteField = (identifier: string) => {
    dispatch(tabActions.setSaved(false));

    dispatch(
      tabActions.nodeInputNestedArrayFilter({
        nodeId: command.node.id,
        fieldId: id,
        nestedFieldName: 'args',
        nestedFieldId: identifier,
      })
    );
  };

  const searchTypeOptions: Option[] = [
    { label: 'string', value: 'string' },
    { label: 'boolean', value: 'boolean' },
    { label: 'enum', value: 'enum' },
    { label: 'number', value: 'number' },
  ];

  if (!enabled) return null;

  return (
    <Container>
      <Arguments>
        <Stack spacing={2} divider={<Divider flexItem />}>
          {args.map(({ id: arg_id, name, description: _description, type, enumerator }) => (
            <ArgumentBlock key={arg_id}>
              <Label title={'JSON Argument'} tooltip={description} onDelete={() => deleteField(arg_id)} />
              <Stack display='grid' gridTemplateColumns='6fr 4fr' gap='8px'>
                <Expression
                  key={`${arg_id}_name`}
                  value={name}
                  dataEdges={dataEdges}
                  title={label}
                  description={'This is the name of the argument as it will be described to the LLM.'}
                  setValue={(value: string) => updateField(arg_id, 'name', value)}
                  options={options}
                  placeholder={'Name'}
                />
                <Select
                  key={`${id}_select`}
                  value={type}
                  setValue={(value: string) => updateField(arg_id, 'type', value)}
                  options={searchTypeOptions}
                  placeholder='Select an index search type'
                />
              </Stack>
              <Expression
                key={`${id}_description`}
                value={_description}
                dataEdges={dataEdges}
                title={label}
                description={
                  'This is the description that the LLM will read to determine how to generate this argument.'
                }
                setValue={(value: string) => updateField(arg_id, 'description', value)}
                options={options}
                placeholder={'Description'}
              />
              {type === 'enum' && (
                <>
                  <Expression
                    key={`${id})enum`}
                    value={enumerator}
                    dataEdges={dataEdges}
                    title={label}
                    description={
                      'This is an enumerator type. You should separate the values using commas.\nFor example: "dog, cat, sheep".'
                    }
                    setValue={(value: string) => updateField(arg_id, 'enumerator', value)}
                    options={options}
                    placeholder={'Enums'}
                  />
                  <Notice error={enumerator?.length > 0 && !enumerator?.includes(',')}>
                    {enumerator?.length > 0 && !enumerator?.includes(',')
                      ? 'An enum type must have at least two values separated by a comma.'
                      : 'Enumerators are separated using a comma (,)'}
                  </Notice>
                </>
              )}
            </ArgumentBlock>
          ))}
        </Stack>
        <Box sx={{ mt: args.length > 0 ? 2 : 0 }}>
          <Button customColor='blue' fullWidth onClick={addArgument}>
            Add JSON Argument
          </Button>
        </Box>
      </Arguments>
    </Container>
  );
};
